import React from 'react';
import { Stack, Button, Typography, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { useParameterizedQuery, useMutation } from 'react-fetching-library';
import { getDocuments, sendDocument, sendSetOfDocumentsAction, sendOrderMail, refreshOrderDocumentAction } from '../../../../../../services/request';
import AgreementButton from './AgreementButton';
import DocumentButton from './DocumentButton';
import PDFPreviewModal from './PDFPreviewModal';
import AttachEmailIcon from '@mui/icons-material/AttachEmail';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SendMessagePreviewModal from './SendMessagePreviewModal';
import toast from 'react-hot-toast';
import { ConfirmDialog } from '../../../../../../components/ConfirmDialog';

const DocumentsButtons = () => {
  const [openPreviewModal, setOpenPreviewModal] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [documentTypeToRefresh, setDocumentTypeToRefresh] = React.useState(null);
  const { module, seasonIdentifier, seasonYear } = useSelector(state => state.app);
  const { order } = useSelector(state => state.orderData);
  const { mutate } = useMutation(sendDocument);
  const { mutate: sendMailMutate } = useMutation(sendOrderMail);
  const { mutate: refreshDocumentMutate } = useMutation(refreshOrderDocumentAction);
  const { query } = useParameterizedQuery(getDocuments);
  const { query: sendAllQuery } = useParameterizedQuery(sendSetOfDocumentsAction);

  const handleClick = async (template_name, fileName, annex_id = 0, preview = false) => {
    const { payload, error, loading } = await query(order.id, template_name, annex_id);
    if (!error && !loading) {
      if (preview) {
        setPreviewUrl(window.URL.createObjectURL(payload));
      } else {
        const url = window.URL.createObjectURL(payload);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style = 'display: none';
        a.href = url;
        a.download = `${fileName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    }
  };

  const handleSend = async (template_name, data, documentName) => {
    const { error, loading } = await mutate(order.id, template_name, data, documentName);

    if (error) {
      toast.error('Nie udało się wysłać dokumentu');
    }
  };

  const handleOpenConfirmDialog = documentType => {
    setDocumentTypeToRefresh(documentType);
    setOpenModal(true);
  };

  const handleConfirmRefresh = async documentType => {
    const { error, loading } = await refreshDocumentMutate(order.id, documentType);

    if (error) {
      toast.error('Nie udało się odświeżyć dokumentu');
    }
    if (!error && !loading) {
      toast.success('Dokument został odświeżony');
    }
    setOpenModal(false);

  };

  const handleSendOrderMail = async data => {
    const { error, loading } = await sendMailMutate(order.id, data);

    if (error) {
      toast.error('Nie udało się wysłać maila');
    }
  };

  const handleSendAll = async data => {
    const { error, loading } = await sendAllQuery(order.id, data);
    if (!error && !loading) {
      toast.success('Komplet dokumentów został wysłany');
      setOpenPreviewModal(false);
    }

    if (error) {
      toast.error('Nie udało się wysłać kompletu dokumentów');
    }
  };

  const getTemplateName = templateName => {
    if (module === 'polkolonie') {
      return `${templateName}_polkolonie`;
    }
    if (module === 'obozy') {
      return `${templateName}_${seasonYear}`;
    }

    return templateName;

  };

  const handleDownloadAll = async () => {
    if (order.order_annexes.length > 0) {
      const annex = order.order_annexes[order.order_annexes.length - 1];
      await handleClick(getTemplateName('agreement_annex'), `Aneks nr ${annex.number} do umowy ${order.agreement_number}`, annex.id);
    } else {
      await handleClick(getTemplateName('agreement'), 'umowa');
    }
    await handleClick(getTemplateName('qualification_card'), 'karta kwalifikacyjna');
    await handleClick(getTemplateName('terms_of_participation'), 'warunki uczestnictwa');
    await handleClick(getTemplateName('clauses'), 'Klauzule inf.odo do umowy_organizacja wypoczynku_SunSport');
  };

  const handleOpenPreviewModal = () => setOpenPreviewModal(true);
  const handleClosePreviewModal = () => setOpenPreviewModal(false);

  const getZsTemplateName = () => {
    if (order.period_display.toLowerCase().includes('arena ninja')) {
      return 'attachment_giewartow_zs_2025_ARENA_NINJA';
    } else if (order.period_display.toLowerCase().includes('sunmagedon')) {
      return 'attachment_mrowki_zs_2025_SUNMAGEDON';
    }
    if (order.resort_name.toLowerCase() === 'mrówki') {
      if (seasonYear < 2027) {
        return `attachment_mrowki_zs_${seasonYear}`;
      } else if (order.period_display.includes('1')) {
        return `attachment_mrowki_zs_1_${seasonYear}`;
      } else if (order.period_display.includes('5')) {
        return `attachment_mrowki_zs_5_${seasonYear}`;
      }

      return `attachment_mrowki_zs_3_${seasonYear}`;
    }

    if (seasonYear < 2027) {
      return `attachment_giewartow_zs_${seasonYear}`;
    } else if (order.period_display.includes('1')) {
      return `attachment_giewartow_zs_1_${seasonYear}`;
    } else if (order.period_display.includes('5')) {
      return `attachment_giewartow_zs_5_${seasonYear}`;
    }

    return `attachment_giewartow_zs_3_${seasonYear}`;
  };

  const attachmentFileNames = [
    order.agreement_number,
    ['l_23', 'l_24'].includes(seasonIdentifier) ? 'Karta kwalifikacyjna' : null,
    'Warunk uczestnictwa',
    'Klauzule inf.odo do umowy_organizacja wypoczynku_SunSport'
  ].filter(Boolean);

  return (
    <Grid container spacing={2} p={4}>
      {module === 'obozy' || module === 'polkolonie' ? (
        <>
          <Grid item xs={12} sm={4} md={2}>
            <AgreementButton order={order} getDocument={handleClick} module={module}/>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <DocumentButton
              name="Karta kwalifikacyjna"
              disabled={order.state !== 'CON'}
              handleDownload={() => handleClick(getTemplateName('qualification_card'), 'karta kwalifikacyjna')}
              handleShow={() => handleClick(getTemplateName('qualification_card'), 'karta kwalifikacyjna', 0, true)}
              handleSend={data => handleSendOrderMail(data)}
              handleRefresh={() => handleOpenConfirmDialog('qualification_card')}
              notificationType={'SQCD'}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <DocumentButton
              name="Warunki uczestnictwa"
              disabled={order.state !== 'CON'}
              handleDownload={() => handleClick(getTemplateName('terms_of_participation'), 'warunki uczestnictwa')}
              handleShow={() => handleClick(getTemplateName('terms_of_participation'), 'warunki uczestnictwa', 0, true)}
              handleSend={data => handleSend(getTemplateName('terms_of_participation'), data, 'warunki uczestnictwa')}
              notificationType={'SDFE'}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <DocumentButton
              name="Klauzule"
              disabled={order.state !== 'CON'}
              handleDownload={() => handleClick(getTemplateName('clauses'), 'klauzule')}
              handleShow={() => handleClick(getTemplateName('clauses'), 'klauzule', 0, true)}
              handleSend={data => handleSend(getTemplateName('clauses'), data, 'klauzule')}
              notificationType={'SDFE'}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Button
              variant="contained"
              disabled={order.state !== 'CON'}
              onClick={order.way_of_sending_contract === 'traditional_mail' ? handleDownloadAll : handleOpenPreviewModal}
              endIcon={order.way_of_sending_contract === 'traditional_mail' ? <FileDownloadIcon /> : <AttachEmailIcon />}
            >
              {order.way_of_sending_contract === 'traditional_mail' ? 'Pobierz dokumenty' : 'Wyślij komplet dokumentów'}
            </Button>
          </Grid>
        </>
      ) : module === 'zielone_szkoly'
        ? (
          <>
            <Grid item xs={12} sm={4} md={4}>
              <AgreementButton order={order} getDocument={handleClick} module={module}/>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <DocumentButton
                name="Załącznik zajęciowy"
                disabled={order.state !== 'CON'}
                handleDownload={() => handleClick(getZsTemplateName(), `załącznik do umowy nr ${order.agreement_number}`)}
                handleShow={() => handleClick(getZsTemplateName(), `załącznik do umowy nr ${order.agreement_number}`, 0, true)}
                handleSend={data => handleSend(getZsTemplateName(), data, `załącznik do umowy nr ${order.agreement_number}`)}
                notificationType="SATT"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <DocumentButton
                name="Mail nauczyciele"
                disabled={order.state !== 'CON'}
                handleSend={data => handleSendOrderMail(data)}
                notificationType="MATE"
                onlySend={true}
              />
            </Grid>
          </>
        ) : module === 'wyjazdy_rodzinne'
          ? (
            <>
              <Grid item xs={12} sm={4} md={4}>
                <AgreementButton order={order} getDocument={handleClick} module={module}/>
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <Button
                  variant="contained"
                  disabled={order.state !== 'CON'}
                  onClick={order.way_of_sending_contract === 'traditional_mail' ? handleDownloadAll : handleOpenPreviewModal}
                  endIcon={order.way_of_sending_contract === 'traditional_mail' ? <FileDownloadIcon /> : <AttachEmailIcon />}
                >
                  {order.way_of_sending_contract === 'traditional_mail' ? 'Pobierz dokumenty' : 'Wyślij komplet dokumentów'}
                </Button>
              </Grid>
            </>
          )
          : null}

      {/* Modale i dialogi */}
      {previewUrl !== null && (
        <PDFPreviewModal url={previewUrl} handleClose={() => setPreviewUrl(null)} />
      )}
      {openPreviewModal && (
        <SendMessagePreviewModal
          handleSend={handleSendAll}
          handleClose={handleClosePreviewModal}
          fileNames={attachmentFileNames}
          type="SSOD"
        />
      )}
      <ConfirmDialog
        title={'Odśwież dokument'}
        children={
          <Typography>
            Czy na pewno chcesz zresetować dokument? Obecne dane zostaną bezpowrotnie utracone. Akcja spowoduje automatyczną wysyłkę e-maila z linkiem do karty
          </Typography>
        }
        open={openModal}
        setOpen={setOpenModal}
        onConfirm={() => handleConfirmRefresh(documentTypeToRefresh)}
      />
    </Grid>

  );
};


export default DocumentsButtons;
